import { z } from 'zod';

export const chatSuggestionSchema = z
  .object({
    id: z.string().regex(/^[a-z][a-z0-9_-]{0,49}$/u),
    label: z.string().trim().min(1).max(80),
    message: z.string().trim().min(1).max(1000),
    requiredAction: z.enum([
      'search_offerings',
      'create_lead',
      'calendar_availability',
    ]),
  })
  .strict();
export const chatPresentationSchema = z
  .object({
    subtitle: z.string().trim().min(1).max(200),
    introduction: z.string().trim().min(1).max(1000),
    suggestions: z
      .array(chatSuggestionSchema)
      .max(3)
      .refine(
        (entries) =>
          new Set(entries.map((entry) => entry.id)).size === entries.length,
        'Suggestion identifiers must be unique.'
      ),
  })
  .strict();
export type ChatPresentation = z.infer<typeof chatPresentationSchema>;
export const apartmentReferenceSchema = z
  .object({
    unitCode: z
      .string()
      .trim()
      .min(1)
      .max(160)
      .refine(
        (value) =>
          [...value].every((character) => character.charCodeAt(0) >= 32),
        'Invalid apartment reference.'
      ),
  })
  .strict();
export type ApartmentReference = z.infer<typeof apartmentReferenceSchema>;
export const resolvedApartmentSchema = apartmentReferenceSchema
  .extend({
    unitId: z.uuid(),
    title: z.string().min(1).max(500),
  })
  .strict();
export type ResolvedApartment = z.infer<typeof resolvedApartmentSchema>;

const apartmentContextLabels = {
  'es-PE': 'Departamento seleccionado',
  'en-US': 'Selected apartment',
} as const;
const apartmentContextPattern = new RegExp(
  `\\n\\n\\[(?:${Object.values(apartmentContextLabels).join('|')}): [^\\]\\n]{1,160}\\]$`,
  'u'
);
// Internal context added by the chat endpoint. It is visible to composition, never classifier input.
export function apartmentContextAnnotation(
  locale: keyof typeof apartmentContextLabels,
  unitCode: string
): string {
  return `\n\n[${apartmentContextLabels[locale]}: ${unitCode}]`;
}
export function customerRequestText(text: string): string {
  return text.replace(apartmentContextPattern, '');
}
